import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { useToast } from '../../hooks/useToast';
import { ListenButton } from '../common/AudioGuide';
import { EmployeeHelpShell } from '../common/EmployeeCodeLegendPanel';
import { FilterIcon, FilterTemplateDropdown, useFilterTemplate } from '../common/FilteringTemplate';
import ExcelJS from 'exceljs';
import EmployeeImportConfirmModal from './EmployeeImportConfirmModal';
import { parseEmployeeImportRows, buildEmployeeImportPreview, incomingToBulkChanges } from '../../utils/employeeImportPreview';

const STATUS_OPTS = [
  { value: 'active', label: 'Active', color: '#047857', bg: '#d1fae5' },
  { value: 'leave', label: 'On Leave', color: '#b45309', bg: '#fef3c7' },
  { value: 'terminated', label: 'Terminated', color: '#b91c1c', bg: '#fee2e2' },
];

const BALL_LABELS = {
  jordan_entra: { label: 'Jordan — Entra email', color: '#1d4ed8' },
  portal_sync: { label: 'Portal — sync user', color: '#7c3aed' },
  role_assignment: { label: 'Portal — assign role', color: '#b45309' },
  ready: { label: 'Ready', color: '#047857' },
  'n/a': { label: '—', color: '#6b7280' },
};

// Discipline = Trade (same thing) — the master keeps ONE field (discipline_code).
const COLS = [
  { key: 'emp_code', label: 'Code', w: 72 },
  { key: 'emp_name', label: 'Name', w: 160 },
  { key: 'empl_email', label: 'Email', w: 200 },
  { key: 'status', label: 'Status', w: 110 },
  { key: 'ball_in_court', label: 'Ball in court', w: 150 },
  { key: 'has_portal_access', label: 'Portal', w: 64 },
  { key: 'dept_code', label: 'Dept', w: 56 },
  { key: 'discipline_code', label: 'Disc (=Trade)', w: 84 },
  { key: 'role_code', label: 'Role', w: 56 },
  { key: 'feeds_indirect', label: 'Indirect', w: 70 },
  { key: 'followup_note', label: 'Note (follow-up)', w: 210 },
  { key: 'phone_us', label: 'US phone', w: 110 },
  { key: 'phone_us_ext', label: 'US ext', w: 60 },
  { key: 'phone_cdn', label: 'CDN phone', w: 110 },
  { key: 'phone_cdn_ext', label: 'CDN ext', w: 60 },
  { key: 'welder_number', label: 'Welder #', w: 80 },
  { key: 'assigned_projects_count', label: 'Projects', w: 70 },
  { key: 'assigned_estimates_count', label: 'Estimates', w: 74 },
];

// Column defs for the shared PMO filter template (item 2, 2026-08-10 round 3)
const FILTER_COLUMNS = COLS.map((c) => ({
  key: c.key,
  label: c.label,
  type: ['assigned_projects_count', 'assigned_estimates_count'].includes(c.key) ? 'number' : 'text',
}));

const COL_WIDTHS_LS_KEY = 'hiringLayoffColWidths';

const inputStyle = {
  width: '100%',
  padding: '2px 5px', // compact rows (item 1: ~10% less height)
  border: '1px solid #d1d5db',
  borderRadius: 4,
  fontSize: '0.78rem',
  boxSizing: 'border-box',
};

/** One box in the graphical process flow */
function FlowNode({ color = '#1e3a5f', bg = '#fff', title, children }) {
  return (
    <div style={{
      border: `2px solid ${color}`, background: bg, borderRadius: 10,
      padding: '10px 16px', textAlign: 'center', maxWidth: 560, width: '100%',
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)', fontSize: 13,
    }}>
      <div style={{ fontWeight: 700, color }}>{title}</div>
      {children && <div style={{ color: '#374151', marginTop: 2, fontSize: 12 }}>{children}</div>}
    </div>
  );
}

function FlowArrow({ label }) {
  return (
    <div style={{ textAlign: 'center', color: '#1e3a5f', padding: '2px 0', lineHeight: 1.1 }}>
      <div style={{ fontSize: 18 }}>▼</div>
      {label && <div style={{ fontSize: 11, color: '#6b7280' }}>{label}</div>}
    </div>
  );
}

/** Graphical hiring / layoff process content for the Help modal */
function HiringProcessContent() {
  const col = { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 };
  return (
    <div>
      <div style={{
        display: 'flex', gap: 14, fontSize: 12, color: '#6b7280', marginBottom: 10, flexWrap: 'wrap',
      }}>
        <span><span style={dotStyle('#7c3aed', '#f5f3ff')} /> Tanya (Accounting)</span>
        <span><span style={dotStyle('#0369a1', '#f0f9ff')} /> Jordan (IT)</span>
        <span><span style={dotStyle('#0f766e', '#f0fdfa')} /> Portal — automatic</span>
        <span><span style={dotStyle('#15803d', '#f0fdf4')} /> Outcome</span>
      </div>

      <h3 style={{ color: '#0f766e', margin: '8px 0' }}>Hiring a new employee</h3>
      <div style={col}>
        <FlowNode color="#7c3aed" bg="#f5f3ff" title="1. Tanya adds the person in Maestro, then here">
          4-digit employee code + name + dept / discipline / role codes (required)
        </FlowNode>
        <FlowArrow label="ball in court: Jordan" />
        <FlowNode color="#0369a1" bg="#f0f9ff" title="2. Jordan creates the company email in Entra ID" />
        <FlowArrow label="ball in court: Portal (automatic)" />
        <FlowNode color="#0f766e" bg="#f0fdfa" title="3. Daily Entra sync fills the email + creates the portal user">
          No fake emails — the account appears when the real mailbox exists
        </FlowNode>
        <FlowArrow label="automatic" />
        <FlowNode color="#0f766e" bg="#f0fdfa" title="4. Time Tracker access granted">
          Every active employee with a code gets Time Tracker
        </FlowNode>
        <FlowArrow />
        <FlowNode color="#15803d" bg="#f0fdf4" title="5. ACTIVE on the single master list">
          Feeds Time Tracker, Indirect Resources, Welder log, EVM, Safety — all from this one list
        </FlowNode>
      </div>

      <h3 style={{ color: '#b91c1c', margin: '16px 0 8px' }}>Layoff / leave</h3>
      <div style={col}>
        <FlowNode color="#7c3aed" bg="#f5f3ff" title="1. Tanya changes Status to Leave or Terminated" />
        <FlowArrow label="automatic" />
        <FlowNode color="#0f766e" bg="#f0fdfa" title="2. Portal access revoked; removed from all active-only dropdowns" />
        <FlowArrow />
        <FlowNode color="#15803d" bg="#f0fdf4" title="3. History preserved">
          Time entries, safety training, and welder continuity stay linked to the code
        </FlowNode>
      </div>

      <h3 style={{ margin: '16px 0 6px' }}>Rules</h3>
      <ul style={{ fontSize: 13, lineHeight: 1.55 }}>
        <li>This dashboard IS the single master (<code>timetracker.lt_employee</code>). No other roster exists; Admin → Users is portal roles only.</li>
        <li>Employee codes are always 4 digits (leading zeros).</li>
        <li>No duplicate employee codes. Emails must be unique among <strong>active / on-leave</strong> people — a terminated row can keep the old mailbox so you can assign it to the live person.</li>
        <li>Every employee must have department, discipline, and role codes — see the Legend tab.</li>
        <li><strong>Discipline = Trade</strong> (same thing, one field). Discipline codes match the Accounting → Coding Structure disciplines.</li>
        <li>The Note column is the follow-up list for Accounting — rows with a note appear in the daily digest email until the note is cleared.</li>
        <li>Every edit here is recorded in the Audit log (who, when, what changed) — see the Audit button.</li>
        <li>Indirect Resources only lists people with the “Indirect” checkbox (foremen and above + staff).</li>
        <li>Welder numbers sync automatically from the Welder Continuity Log.</li>
      </ul>
    </div>
  );
}

function dotStyle(color, bg) {
  return {
    display: 'inline-block', width: 11, height: 11, borderRadius: 3,
    border: `2px solid ${color}`, background: bg, marginRight: 5, verticalAlign: 'middle',
  };
}

/** Audit log modal — reads timetracker.lt_employee_audit_log (item 13) */
function AuditLogModal({ open, onClose, instance, accounts }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await authenticatedApiCall(instance, accounts, 'GET', '/employees/audit?limit=300');
        if (!cancelled && res.data.success) setRows(res.data.data || []);
      } catch (e) {
        console.error('audit load failed', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, instance, accounts]);

  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => [r.emp_code, r.emp_name, r.user_email, r.user_name, r.action_type]
      .some((v) => String(v || '').toLowerCase().includes(q)));
  }, [rows, filter]);

  if (!open) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 10, width: 'min(960px, 94vw)', maxHeight: '86vh', display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: '1px solid #e5e7eb' }}>
          <h3 style={{ margin: 0, color: '#1e3a5f', fontSize: 16 }}>Employee master — Audit log</h3>
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by code, name, user…"
            style={{ ...inputStyle, width: 220, padding: '6px 8px' }}
          />
          <span style={{ fontSize: 12, color: '#6b7280' }}>{shown.length} entr{shown.length === 1 ? 'y' : 'ies'}</span>
          <button type="button" onClick={onClose} style={{ marginLeft: 'auto', border: 'none', background: 'none', fontSize: 20, cursor: 'pointer', color: '#6b7280' }}>×</button>
        </div>
        <div style={{ overflow: 'auto', padding: '0 16px 16px' }}>
          {loading ? (
            <div style={{ padding: 24 }}>Loading…</div>
          ) : (
            <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
              <thead>
                <tr style={{ position: 'sticky', top: 0, background: '#f9fafb' }}>
                  {['When', 'User', 'Action', 'Code', 'Name', 'Changes'].map((h) => (
                    <th key={h} style={{ padding: '8px 6px', borderBottom: '1px solid #e5e7eb', textAlign: 'left', color: '#374151' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => (
                  <tr key={r.id}>
                    <td style={{ padding: '5px 6px', borderBottom: '1px solid #f3f4f6', whiteSpace: 'nowrap' }}>{new Date(r.created_at).toLocaleString()}</td>
                    <td style={{ padding: '5px 6px', borderBottom: '1px solid #f3f4f6' }}>{r.user_name || r.user_email || '—'}</td>
                    <td style={{ padding: '5px 6px', borderBottom: '1px solid #f3f4f6', fontWeight: 600, color: r.action_type === 'CREATE' ? '#047857' : '#1d4ed8' }}>{r.action_type}</td>
                    <td style={{ padding: '5px 6px', borderBottom: '1px solid #f3f4f6' }}>{r.emp_code}</td>
                    <td style={{ padding: '5px 6px', borderBottom: '1px solid #f3f4f6' }}>{r.emp_name}</td>
                    <td style={{ padding: '5px 6px', borderBottom: '1px solid #f3f4f6' }}>
                      {r.changes && typeof r.changes === 'object'
                        ? Object.entries(r.changes).map(([field, v]) => (
                          <div key={field}>
                            <code style={{ background: '#f3f4f6', padding: '0 4px', borderRadius: 3 }}>{field}</code>
                            {' '}<span style={{ color: '#b91c1c' }}>{String(v?.from ?? '—') || '(blank)'}</span>
                            {' → '}<span style={{ color: '#047857' }}>{String(v?.to ?? '—') || '(blank)'}</span>
                          </div>
                        ))
                        : '—'}
                    </td>
                  </tr>
                ))}
                {!shown.length && (
                  <tr><td colSpan={6} style={{ padding: 16, color: '#6b7280' }}>No audit entries.</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Master employee list — Accounting Hiring / Layoff dashboard.
 * Also reused from Admin → Employees (same SSOT).
 */
const HiringLayoffDashboard = ({ embedded = false, onBack }) => {
  const { instance, accounts } = useMsal();
  const { showToast } = useToast();
  const [rows, setRows] = useState([]);
  const [legend, setLegend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [noteFilter, setNoteFilter] = useState(false); // only rows with a follow-up note
  const [sortBy, setSortBy] = useState({ key: null, dir: 1 });
  const [edited, setEdited] = useState({});
  const [newRows, setNewRows] = useState([]);
  const [helpOpen, setHelpOpen] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const importInputRef = useRef(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importFileName, setImportFileName] = useState('');
  const [importIncoming, setImportIncoming] = useState([]);
  const [importing, setImporting] = useState(false);

  // User-adjustable column widths, persisted per browser (item 5)
  const [colWidths, setColWidths] = useState(() => {
    const base = COLS.reduce((acc, c) => ({ ...acc, [c.key]: c.w }), {});
    try {
      const saved = JSON.parse(localStorage.getItem(COL_WIDTHS_LS_KEY) || '{}');
      Object.keys(saved).forEach((k) => { if (base[k] && Number(saved[k]) >= 40) base[k] = Number(saved[k]); });
    } catch (_) { /* corrupted localStorage — use defaults */ }
    return base;
  });
  const resizeRef = useRef(null);
  const startResize = (e, key) => {
    e.preventDefault();
    e.stopPropagation();
    resizeRef.current = { key, startX: e.clientX, startW: colWidths[key] };
    const onMove = (ev) => {
      const s = resizeRef.current;
      if (!s) return;
      setColWidths((prev) => ({ ...prev, [s.key]: Math.max(40, s.startW + (ev.clientX - s.startX)) }));
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      resizeRef.current = null;
      setColWidths((prev) => {
        try { localStorage.setItem(COL_WIDTHS_LS_KEY, JSON.stringify(prev)); } catch (_) { /* quota */ }
        return prev;
      });
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [empRes, legendRes] = await Promise.all([
        authenticatedApiCall(instance, accounts, 'GET', '/employees'),
        authenticatedApiCall(instance, accounts, 'GET', '/employees/code-legend'),
      ]);
      if (empRes.data.success) {
        setRows(empRes.data.data || []);
        setEdited({});
        setNewRows([]);
      }
      if (legendRes.data.success) setLegend(legendRes.data.data);
    } catch (e) {
      console.error(e);
      showToast('Failed to load employees', 'error');
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, showToast]);

  useEffect(() => { load(); }, [load]);

  const hasUnsaved = newRows.length > 0 || Object.keys(edited).length > 0;
  useEffect(() => {
    if (!hasUnsaved) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [hasUnsaved]);

  const mergedRows = useMemo(() => [
    ...newRows,
    ...rows.map((r) => ({ ...r, ...(edited[r.emp_code] || {}) })),
  ], [rows, edited, newRows]);

  // Shared PMO-style per-column filters (item 2) — funnel icon on every header
  const {
    filteredItems, columnFilters, openFilterColumn, filterSearch,
    filterMode, filterText, filterButtonPositions,
    setOpenFilterColumn, setFilterSearch, getUniqueValues,
    handleFilterToggle, selectAllColumnFilter, clearColumnFilter,
    applyTextFilter, openFilterAtPosition, clearAllFilters, numericColumnKeys, columnLabels,
  } = useFilterTemplate({ items: mergedRows, columns: FILTER_COLUMNS });

  const columnFiltersActive = COLS.some((c) => columnFilters[c.key] !== undefined || filterMode[c.key]);

  const displayRows = useMemo(() => {
    const tokens = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const filtered = filteredItems.filter((r) => {
      if (statusFilter !== 'all' && (r.status || 'active') !== statusFilter) return false;
      if (noteFilter && !String(r.followup_note || '').trim()) return false;
      if (!tokens.length) return true;
      const haystack = [
        r.emp_code, r.emp_name, r.empl_email, r.portal_email,
        r.dept_code, r.discipline_code, r.role_code, r.division_name, r.followup_note,
        r.phone_us, r.phone_cdn, r.phone_us_ext, r.phone_cdn_ext, r.welder_number,
        String(r.emp_code || '').replace(/^0+/, ''), // "200" finds "0200"
      ].map((v) => String(v || '').toLowerCase());
      return tokens.every((t) => haystack.some((h) => h.includes(t)));
    });
    if (!sortBy.key) return filtered;
    const numeric = ['assigned_projects_count', 'assigned_estimates_count'].includes(sortBy.key);
    const boolCols = ['has_portal_access', 'feeds_indirect'];
    return [...filtered].sort((a, b) => {
      let av = a[sortBy.key]; let bv = b[sortBy.key];
      if (numeric) { av = Number(av) || 0; bv = Number(bv) || 0; return (av - bv) * sortBy.dir; }
      if (boolCols.includes(sortBy.key)) { av = a[sortBy.key] ? 1 : 0; bv = b[sortBy.key] ? 1 : 0; return (av - bv) * sortBy.dir; }
      av = String(av || '').toLowerCase(); bv = String(bv || '').toLowerCase();
      if (av === bv) return 0;
      if (!av) return 1; // blanks last either direction
      if (!bv) return -1;
      return (av < bv ? -1 : 1) * sortBy.dir;
    });
  }, [filteredItems, search, statusFilter, noteFilter, sortBy]);

  const stats = useMemo(() => {
    const active = rows.filter((r) => r.status === 'active').length;
    const portal = rows.filter((r) => r.has_portal_access).length;
    const waitingEntra = rows.filter((r) => r.ball_in_court === 'jordan_entra').length;
    return { total: rows.length, active, portal, waitingEntra };
  }, [rows]);

  const setField = (id, field, value, isNew) => {
    if (isNew) {
      setNewRows((prev) => prev.map((r) => (r.tempId === id ? { ...r, [field]: value } : r)));
    } else {
      setEdited((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
    }
  };

  const addRow = () => {
    setNewRows((prev) => [
      {
        tempId: `new_${Date.now()}`,
        isNew: true,
        emp_code: '',
        first_name: '',
        last_name: '',
        emp_name: '',
        empl_email: '',
        status: 'active',
        dept_code: '',
        discipline_code: '',
        role_code: '',
        feeds_indirect: false,
        phone_us: '',
        phone_us_ext: '',
        phone_cdn: '',
        phone_cdn_ext: '',
        welder_number: '',
        followup_note: '',
        payroll_company: 'TBD',
      },
      ...prev,
    ]);
  };

  const parseImportFile = async (file) => {
    if (!file) return;
    try {
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(await file.arrayBuffer());
      const ws = workbook.worksheets[0];
      if (!ws) {
        showToast('No worksheet found in that file', 'error');
        return;
      }
      const headers = [];
      ws.getRow(1).eachCell((cell, col) => { headers[col - 1] = cell.value != null ? String(cell.value) : ''; });
      const dataRows = [];
      ws.eachRow((row, rowNumber) => {
        if (rowNumber === 1) return;
        const vals = [];
        row.eachCell({ includeEmpty: true }, (cell, col) => {
          const v = cell.value;
          vals[col - 1] = v && typeof v === 'object' && v.text ? v.text : v;
        });
        dataRows.push(vals);
      });
      const parsed = parseEmployeeImportRows(headers, dataRows);
      if (parsed.error) {
        showToast(parsed.error, 'error');
        return;
      }
      setImportIncoming(parsed.rows);
      setImportFileName(file.name);
      setImportPreview(buildEmployeeImportPreview(parsed.rows, rows));
    } catch (e) {
      console.error(e);
      showToast('Could not read that Excel file', 'error');
    } finally {
      if (importInputRef.current) importInputRef.current.value = '';
    }
  };

  const confirmImport = async () => {
    if (!importIncoming.length) return;
    setImporting(true);
    try {
      const existingCodes = new Set(rows.map((r) => r.emp_code));
      const changes = incomingToBulkChanges(importIncoming, { existingCodes });
      const res = await authenticatedApiCall(instance, accounts, 'POST', '/employees/bulk-save', { changes });
      if (res.data.success) {
        const failed = (res.data.errors || []).length;
        showToast(res.data.message || `Imported ${changes.length} row(s)`, failed ? 'warning' : 'success');
        setImportPreview(null);
        setImportIncoming([]);
        await load();
      } else {
        showToast(res.data.message || 'Import failed', 'error');
      }
    } catch (e) {
      console.error(e);
      showToast(e.response?.data?.message || 'Import failed', 'error');
    } finally {
      setImporting(false);
    }
  };

  const exportExcel = async () => {
    try {
      const res = await authenticatedApiCall(
        instance, accounts, 'GET', '/employees/export-direct',
        null, { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `employee_master_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      showToast('Excel export failed', 'error');
    }
  };

  const save = async () => {
    // Validate new rows before anything is sent (4-digit code + org codes required)
    const problems = [];
    const normalizedNew = newRows.map((r) => {
      const raw = String(r.emp_code || '').trim();
      const padded = /^\d{1,4}$/.test(raw) ? raw.padStart(4, '0') : raw;
      const who = `${r.last_name || '?'}, ${r.first_name || '?'}`;
      if (!/^\d{4}$/.test(padded)) problems.push(`${who}: employee code must be 4 digits`);
      if (!r.first_name?.trim() || !r.last_name?.trim()) problems.push(`Code ${padded || '?'}: first and last name required`);
      // Org codes are strongly recommended but no longer block the save —
      // blocking them was why new people vanished after a refresh.
      if (!r.dept_code?.trim() || !r.discipline_code?.trim() || !r.role_code?.trim()) {
        r.followup_note = r.followup_note?.trim()
          || 'Needs dept / discipline (= trade) / role codes from Accounting.';
      }
      return { ...r, emp_code: padded };
    });
    if (problems.length) {
      showToast(problems[0] + (problems.length > 1 ? ` (+${problems.length - 1} more)` : ''), 'error');
      return;
    }

    const changes = [];
    Object.entries(edited).forEach(([emp_code, patch]) => {
      const base = rows.find((r) => r.emp_code === emp_code);
      if (!base) return;
      const merged = { ...base, ...patch };
      const nameParts = (merged.emp_name || '').split(',').map((s) => s.trim());
      changes.push({
        emp_code,
        isNew: false,
        first_name: merged.first_name || nameParts[1] || '',
        last_name: merged.last_name || nameParts[0] || '',
        empl_email: merged.empl_email,
        followup_note: merged.followup_note,
        payroll_company: merged.payroll_company,
        status: merged.status,
        dept_code: merged.dept_code,
        discipline_code: merged.discipline_code,
        role_code: merged.role_code,
        feeds_indirect: merged.feeds_indirect,
        phone_us: merged.phone_us,
        phone_us_ext: merged.phone_us_ext,
        phone_cdn: merged.phone_cdn,
        phone_cdn_ext: merged.phone_cdn_ext,
        welder_number: merged.welder_number,
        is_active: merged.status === 'active',
      });
    });
    normalizedNew.forEach((r) => {
      changes.push({
        ...r,
        isNew: true,
        first_name: r.first_name,
        last_name: r.last_name,
        is_active: r.status === 'active',
      });
    });
    if (!changes.length) {
      showToast('No changes to save', 'info');
      return;
    }
    const liveEmails = new Map();
    for (const r of mergedRows) {
      if ((r.status || 'active') === 'terminated') continue;
      const em = String(r.empl_email || '').trim().toLowerCase();
      if (!em) continue;
      if (liveEmails.has(em) && liveEmails.get(em) !== r.emp_code) {
        showToast(`Email ${em} is on both ${liveEmails.get(em)} and ${r.emp_code}. Terminate one or clear the extra copy first.`, 'error');
        return;
      }
      liveEmails.set(em, r.emp_code);
    }
    setSaving(true);
    try {
      const res = await authenticatedApiCall(instance, accounts, 'POST', '/employees/bulk-save', { changes });
      if (res.data.success) {
        const failed = (res.data.errors || []).length;
        showToast(
          res.data.message || (failed ? `Saved with ${failed} error(s)` : `Saved ${changes.length} change(s)`),
          failed ? 'warning' : 'success'
        );
        await load();
      } else {
        showToast(res.data.message || 'Save failed', 'error');
      }
    } catch (e) {
      console.error(e);
      showToast(e.response?.data?.message || 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const totalWidth = COLS.reduce((s, c) => s + colWidths[c.key], 0);
  const td = (key) => ({
    width: colWidths[key], minWidth: colWidths[key],
    padding: '2px 4px', // compact rows (item 1)
    borderBottom: '1px solid #f3f4f6', verticalAlign: 'middle',
  });

  return (
    <div style={{ padding: embedded ? '1rem' : '1.25rem 1.5rem', background: '#f3f4f6', minHeight: embedded ? 'auto' : '100%' }}>
      {/* Row 1: title + all action buttons (compact — no wasted band, item 12) */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          {onBack && (
            <button type="button" onClick={onBack} style={{ border: 'none', background: 'none', color: '#1e3a5f', cursor: 'pointer', padding: 0 }}>← Back</button>
          )}
          <h1 style={{ margin: 0, fontSize: '1.3rem', color: '#1e3a5f' }}>Hiring / Layoff</h1>
          <span style={{ color: '#6b7280', fontSize: 12 }}>
            Master employee list (single source of truth). Green rows are not in the database until you click Save changes.
          </span>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <button type="button" onClick={() => setHelpOpen(true)} style={btnSecondary}>❓ Help</button>
          <ListenButton compact guideId="hiring-layoff-help" title="Hiring Layoff Help" accent="#1e3a5f" />
          <input
            ref={importInputRef}
            type="file"
            accept=".xlsx,.xls"
            style={{ display: 'none' }}
            onChange={(e) => parseImportFile(e.target.files?.[0])}
          />
          <button type="button" onClick={() => importInputRef.current?.click()} style={btnSecondary}>⬆ Import</button>
          <button type="button" onClick={exportExcel} style={btnSecondary}>⬇ Excel</button>
          <button type="button" onClick={() => setAuditOpen(true)} style={btnSecondary} title="Who changed what and when">🕑 Audit</button>
          <button type="button" onClick={addRow} style={btnSecondary}>Add employee</button>
          <button type="button" onClick={load} style={btnSecondary}>Refresh</button>
          <button type="button" onClick={save} disabled={saving} style={btnPrimary}>{saving ? 'Saving…' : 'Save changes'}</button>
        </div>
      </div>

      {/* Row 2: stats + search + filters in one line (items 10 + 12) */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <Stat label="Total" value={stats.total} />
        <Stat label="Active" value={stats.active} />
        <Stat label="Portal" value={stats.portal} />
        <Stat label="Awaiting Entra" value={stats.waitingEntra} />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search code, name, email, codes, notes…"
          style={{ ...inputStyle, width: 240, padding: '8px 10px' }}
        />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ ...inputStyle, width: 120, padding: '8px 10px' }} title="Status filter">
          <option value="all">All statuses</option>
          {STATUS_OPTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#374151', fontWeight: 600, cursor: 'pointer' }} title="Show only rows with an open follow-up note">
          <input type="checkbox" checked={noteFilter} onChange={(e) => setNoteFilter(e.target.checked)} />
          Follow-ups
        </label>
        {columnFiltersActive && (
          <button
            type="button"
            onClick={clearAllFilters}
            style={{ padding: '4px 10px', background: '#fee2e2', color: '#b91c1c', border: '1px solid #fca5a5', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 12 }}
            title="Clear all column filters"
          >
            × Clear column filters
          </button>
        )}
        <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>
          {displayRows.length} of {rows.length + newRows.length}
        </span>
        {hasUnsaved && (
          <span style={{ fontSize: 12, color: '#b45309', fontWeight: 700, background: '#fef3c7', padding: '4px 8px', borderRadius: 6 }}>
            Unsaved — click Save changes or these names disappear when you leave
          </span>
        )}
      </div>

      <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e5e7eb', overflow: 'auto', maxHeight: 'calc(100vh - 280px)' }}>
        {loading ? (
          <div style={{ padding: 24 }}>Loading…</div>
        ) : (
          <table style={{ borderCollapse: 'collapse', minWidth: totalWidth, width: '100%', fontSize: 12 }}>
            <thead>
              <tr style={{ position: 'sticky', top: 0, background: '#f9fafb', zIndex: 1 }}>
                {COLS.map((c) => (
                  <th
                    key={c.key}
                    onClick={() => setSortBy((prev) => (prev.key === c.key ? { key: c.key, dir: -prev.dir } : { key: c.key, dir: 1 }))}
                    title="Click to sort · funnel to filter · drag edge to resize"
                    style={{ position: 'relative', width: colWidths[c.key], minWidth: colWidths[c.key], padding: '6px 14px 6px 6px', borderBottom: '1px solid #e5e7eb', textAlign: 'left', fontWeight: 600, color: '#374151', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                  >
                    {c.label}{sortBy.key === c.key ? (sortBy.dir === 1 ? ' ▲' : ' ▼') : ''}
                    <span
                      onClick={(e) => { e.stopPropagation(); openFilterAtPosition(e, c.key); }}
                      style={{ marginLeft: 4, cursor: 'pointer', display: 'inline-flex', verticalAlign: 'middle' }}
                      title={`Filter ${c.label}`}
                    >
                      <FilterIcon isActive={columnFilters[c.key] !== undefined || !!filterMode[c.key]} color="#6b7280" />
                    </span>
                    {/* drag handle — resizable columns (item 5) */}
                    <span
                      onMouseDown={(e) => startResize(e, c.key)}
                      onClick={(e) => e.stopPropagation()}
                      style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 7, cursor: 'col-resize', borderRight: '2px solid transparent' }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderRight = '2px solid #94a3b8'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderRight = '2px solid transparent'; }}
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayRows.map((r, idx) => {
                const id = r.isNew ? r.tempId : r.emp_code;
                const st = STATUS_OPTS.find((s) => s.value === (r.status || 'active')) || STATUS_OPTS[0];
                const ball = BALL_LABELS[r.ball_in_court] || BALL_LABELS['n/a'];
                return (
                  <tr key={id} style={{ background: r.isNew ? '#f0fdf4' : (idx % 2 ? '#fafafa' : '#fff') }}>
                    <td style={td('emp_code')}>
                      <input
                        style={{ ...inputStyle, background: r.isNew ? '#fff' : '#f3f4f6' }}
                        value={r.isNew ? r.emp_code : r.emp_code}
                        readOnly={!r.isNew}
                        onChange={(e) => setField(id, 'emp_code', e.target.value, r.isNew)}
                        placeholder="0000"
                      />
                    </td>
                    <td style={td('emp_name')}>
                      {r.isNew ? (
                        <div style={{ display: 'flex', gap: 4 }}>
                          <input style={inputStyle} placeholder="Last" value={r.last_name || ''} onChange={(e) => setField(id, 'last_name', e.target.value, true)} />
                          <input style={inputStyle} placeholder="First" value={r.first_name || ''} onChange={(e) => setField(id, 'first_name', e.target.value, true)} />
                        </div>
                      ) : (
                        <input
                          style={inputStyle}
                          value={r.emp_name || ''}
                          onChange={(e) => {
                            const parts = e.target.value.split(',').map((s) => s.trim());
                            setField(id, 'emp_name', e.target.value, false);
                            setField(id, 'last_name', parts[0] || '', false);
                            setField(id, 'first_name', parts[1] || '', false);
                          }}
                        />
                      )}
                    </td>
                    <td style={td('empl_email')}>
                      <input style={inputStyle} value={r.empl_email || ''} onChange={(e) => setField(id, 'empl_email', e.target.value, r.isNew)} />
                    </td>
                    <td style={td('status')}>
                      <select
                        value={r.status || 'active'}
                        onChange={(e) => setField(id, 'status', e.target.value, r.isNew)}
                        style={{ ...inputStyle, background: st.bg, color: st.color, fontWeight: 600 }}
                      >
                        {STATUS_OPTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    </td>
                    <td style={{ ...td('ball_in_court'), color: ball.color, fontWeight: 600 }}>{ball.label}</td>
                    <td style={{ ...td('has_portal_access'), textAlign: 'center', fontWeight: 700 }}>
                      {r.isNew ? '—' : (r.has_portal_access ? 'Y' : 'N')}
                    </td>
                    {['dept_code', 'discipline_code', 'role_code'].map((k) => (
                      <td key={k} style={td(k)}>
                        <input style={inputStyle} value={r[k] || ''} maxLength={3} onChange={(e) => setField(id, k, e.target.value.toUpperCase(), r.isNew)} />
                      </td>
                    ))}
                    <td style={{ ...td('feeds_indirect'), textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={!!r.feeds_indirect}
                        onChange={(e) => setField(id, 'feeds_indirect', e.target.checked, r.isNew)}
                      />
                    </td>
                    <td style={td('followup_note')}>
                      <input
                        style={{ ...inputStyle, background: String(r.followup_note || '').trim() ? '#fffbeb' : undefined }}
                        value={r.followup_note || ''}
                        title={r.followup_note || 'Open follow-up for Accounting — clear once confirmed'}
                        placeholder=""
                        onChange={(e) => setField(id, 'followup_note', e.target.value, r.isNew)}
                      />
                    </td>
                    {['phone_us', 'phone_us_ext', 'phone_cdn', 'phone_cdn_ext', 'welder_number'].map((k) => (
                      <td key={k} style={td(k)}>
                        <input style={inputStyle} value={r[k] || ''} onChange={(e) => setField(id, k, e.target.value, r.isNew)} />
                      </td>
                    ))}
                    <td style={{ ...td('assigned_projects_count'), textAlign: 'center' }}>{r.assigned_projects_count ?? '—'}</td>
                    <td style={{ ...td('assigned_estimates_count'), textAlign: 'center' }}>{r.assigned_estimates_count ?? '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Shared per-column filter dropdown (same template as PMO) */}
      {openFilterColumn && (
        <FilterTemplateDropdown
          columnKey={openFilterColumn}
          columnLabel={columnLabels?.[openFilterColumn] || COLS.find((c) => c.key === openFilterColumn)?.label || openFilterColumn}
          showTextFilters={true}
          isNumericColumn={(numericColumnKeys || []).includes(openFilterColumn)}
          columnFilters={columnFilters}
          filterMode={filterMode}
          filterText={filterText}
          filterSearch={filterSearch}
          filterButtonPositions={filterButtonPositions}
          setOpenFilterColumn={setOpenFilterColumn}
          setFilterSearch={setFilterSearch}
          getUniqueValues={getUniqueValues}
          handleFilterToggle={handleFilterToggle}
          selectAllColumnFilter={selectAllColumnFilter}
          clearColumnFilter={clearColumnFilter}
          applyTextFilter={applyTextFilter}
        />
      )}

      <AuditLogModal open={auditOpen} onClose={() => setAuditOpen(false)} instance={instance} accounts={accounts} />
      <EmployeeImportConfirmModal
        open={!!importPreview}
        preview={importPreview}
        fileName={importFileName}
        applying={importing}
        onCancel={() => { setImportPreview(null); setImportIncoming([]); }}
        onConfirm={confirmImport}
      />

      <EmployeeHelpShell
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        title="Hiring / Layoff — Help"
        accent="#1e3a5f"
        guideId="hiring-layoff-help"
        ListenButton={ListenButton}
        legend={legend}
        processContent={<HiringProcessContent />}
      />
    </div>
  );
};

function Stat({ label, value }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '4px 10px', display: 'flex', alignItems: 'baseline', gap: 6 }}>
      <span style={{ fontSize: 15, fontWeight: 700, color: '#1e3a5f' }}>{value}</span>
      <span style={{ fontSize: 11, color: '#6b7280' }}>{label}</span>
    </div>
  );
}

const btnPrimary = {
  padding: '8px 14px', background: '#1e3a5f', color: '#fff', border: 'none', borderRadius: 6,
  cursor: 'pointer', fontWeight: 600, fontSize: 13,
};
const btnSecondary = {
  padding: '8px 14px', background: '#fff', color: '#1e3a5f', border: '1px solid #cbd5e1', borderRadius: 6,
  cursor: 'pointer', fontWeight: 600, fontSize: 13,
};

export default HiringLayoffDashboard;
